import { useEffect, useMemo } from 'react'
import * as THREE from 'three'
import {
  comprimento,
  multiplicar,
  normalizar,
  paraArray,
  somar,
  subtrair,
  type Vetor3,
} from '../../dominio/geometria/vetor3'
import { MaterialPeca } from './Peca'
import { useMapeamentoEstrutural } from '../veiculo/contextoDeformacao'
import { cortarExtremos, emendasDaPolilinha, planejarTrecho, type Emendas } from './emendaDeBarras'

const CIMA = new THREE.Vector3(0, 1, 0)

/** Orientação que alinha o eixo +Y local à direção `de → para`. */
export function orientacaoEntre(de: Vetor3, para: Vetor3): [number, number, number, number] {
  const delta = subtrair(para, de)
  if (comprimento(delta) < 1e-9) return [0, 0, 0, 1]
  const d = normalizar(delta)
  const q = new THREE.Quaternion().setFromUnitVectors(CIMA, new THREE.Vector3(d.x, d.y, d.z))
  return [q.x, q.y, q.z, q.w]
}

export const pontoMedio = (de: Vetor3, para: Vetor3): Vetor3 =>
  somar(de, multiplicar(subtrair(para, de), 0.5))

interface BarraProps {
  id: string
  de: Vetor3
  para: Vetor3
  largura: number
  espessura: number
  corBase?: string
  /**
   * Estende os extremos LIVRES, metade em cada, para a junção com outro elemento
   * não abrir fresta. Um extremo com emenda não estende: ele é cortado em
   * esquadria e encosta exatamente no trecho vizinho.
   */
  folga?: number
  cilindrica?: boolean
  /** Direção dos trechos vizinhos, para cortar as pontas em esquadria (ver `emendasDaPolilinha`). */
  emendas?: Emendas
}

/** comprimento máximo de cada trecho de uma barra deformada */
const TRECHO_DEFORMADO_M = 0.3

/**
 * Barra estrutural entre dois pontos.
 *
 * Colunas, soleiras, longarinas, travessas e barras de porta são todas a mesma
 * primitiva: isso mantém a estrutura coerente e evita Euler escrito à mão.
 *
 * Dentro de um `MapeamentoEstrutural` (veículo deformado na Cinemática), a
 * barra é dividida em trechos e cada ponto passa pelo campo de deformação: a
 * longarina flamba, a coluna B inclina — com as mesmas funções que dobram a
 * chapa em volta. Fora dele, é desenhada exatamente como sempre.
 */
export function Barra(props: BarraProps) {
  const mapeamento = useMapeamentoEstrutural()
  if (!mapeamento) return <TrechoBarra {...props} />

  const { de, para, folga = 0 } = props
  const delta = subtrair(para, de)
  const trechos = Math.max(1, Math.min(6, Math.ceil(comprimento(delta) / TRECHO_DEFORMADO_M)))
  const pontos = Array.from({ length: trechos + 1 }, (_, i) =>
    mapeamento.mapear(somar(de, multiplicar(delta, i / trechos))),
  )
  // quanto cada ponto se deslocou: só os trechos deformados vão ao raio-X
  const danoNoPonto = mapeamento.raioX
    ? Array.from({ length: trechos + 1 }, (_, i) =>
        comprimento(mapeamento.deslocamento(somar(de, multiplicar(delta, i / trechos)))),
      )
    : []
  // os trechos da barra deformada também se encontram em esquadria; só os dois
  // extremos da barra inteira mantêm a emenda (ou a falta dela) que a barra recebeu
  const emendasDosTrechos = emendasDaPolilinha(pontos).map((e, i): Emendas => ({
    inicio: i === 0 ? props.emendas?.inicio : e.inicio,
    fim: i === trechos - 1 ? props.emendas?.fim : e.fim,
  }))
  return (
    <group>
      {pontos.slice(0, -1).map((inicio, i) => (
        <TrechoBarra
          key={i}
          {...props}
          de={inicio}
          para={pontos[i + 1]!}
          emendas={emendasDosTrechos[i]}
          folga={trechos === 1 ? folga : Math.max(folga / trechos, 0.015)}
        />
      ))}
      {mapeamento.raioX &&
        pontos.slice(0, -1).map((inicio, i) => {
          const dano = Math.max(danoNoPonto[i]!, danoNoPonto[i + 1]!)
          if (dano <= LIMIAR_RAIO_X_M) return null
          return (
            <TrechoRaioX
              key={`raioX:${i}`}
              de={inicio}
              para={pontos[i + 1]!}
              largura={props.largura}
              espessura={props.espessura}
              intensidade={Math.min(1, dano / 0.2)}
            />
          )
        })}
    </group>
  )
}

/** a partir de quanto deslocamento uma barra conta como danificada, em metros */
const LIMIAR_RAIO_X_M = 0.02
const COR_DANO_LEVE = new THREE.Color('#fbbf24')
const COR_DANO_SEVERO = new THREE.Color('#dc2626')

/**
 * Trecho de barra desenhado por cima de tudo (sem teste de profundidade):
 * com a lataria sólida, é assim que a longarina flambada e a coluna B
 * inclinada continuam visíveis através da chapa. Âmbar = pouco, vermelho = muito.
 */
function TrechoRaioX({
  de,
  para,
  largura,
  espessura,
  intensidade,
}: {
  de: Vetor3
  para: Vetor3
  largura: number
  espessura: number
  intensidade: number
}) {
  const orientacao = useMemo(() => orientacaoEntre(de, para), [de, para])
  const cor = useMemo(() => COR_DANO_LEVE.clone().lerp(COR_DANO_SEVERO, intensidade), [intensidade])
  return (
    <mesh position={paraArray(pontoMedio(de, para))} quaternion={orientacao} renderOrder={12}>
      <boxGeometry args={[largura * 0.8, comprimento(subtrair(para, de)) + 0.01, espessura * 0.8]} />
      <meshBasicMaterial color={cor} transparent opacity={0.8} depthTest={false} depthWrite={false} />
    </mesh>
  )
}

function TrechoBarra({
  id,
  de,
  para,
  largura,
  espessura,
  corBase,
  folga = 0,
  cilindrica = false,
  emendas,
}: BarraProps) {
  const orientacao = useMemo(() => orientacaoEntre(de, para), [de, para])
  const plano = planejarTrecho(de, para, folga, cilindrica ? undefined : emendas, new THREE.Quaternion(...orientacao))
  const { tamanho, centro } = plano
  const chaveCorte = `${plano.corteInicio?.toArray().join(',') ?? '-'}|${plano.corteFim?.toArray().join(',') ?? '-'}`

  // Sem esquadria a caixa continua sendo a do JSX; com ela, a mesma caixa já cortada.
  const geometria = useMemo(
    () =>
      plano.corteInicio || plano.corteFim
        ? cortarExtremos(new THREE.BoxGeometry(largura, tamanho, espessura), tamanho, plano.corteInicio, plano.corteFim)
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `chaveCorte` resume os dois cortes
    [largura, tamanho, espessura, chaveCorte],
  )
  useEffect(() => () => geometria?.dispose(), [geometria])

  return (
    <mesh position={paraArray(centro)} quaternion={orientacao}>
      {cilindrica ? (
        <cylinderGeometry args={[largura / 2, largura / 2, tamanho, 12]} />
      ) : geometria ? (
        <primitive object={geometria} attach="geometry" />
      ) : (
        <boxGeometry args={[largura, tamanho, espessura]} />
      )}
      <MaterialPeca id={id} corBase={corBase} />
    </mesh>
  )
}
